import React, { useState } from "react";
import axios from "axios";
import { LANDING } from "@/constants/testIds";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MembersAccess({ onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.name.trim() || !form.email.trim()) {
      setError("Name and email are required.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
      });
      setDone(true);
      toast.success("You're on the list.", {
        description: "Members-only release notifications will arrive at your inbox.",
      });
      onSuccess && onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Something went wrong. Try again in a moment.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="members"
      data-testid={LANDING.membersSection}
      className="relative bg-[hsl(var(--scrm-bg))] border-t hairline"
    >
      <div className="px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-4">
              ✦ Members Access
            </div>
            <h2 className="font-display uppercase tracking-mega text-[hsl(var(--scrm-cream))] text-4xl sm:text-5xl md:text-6xl leading-[0.92] mb-6">
              Request<br />access.
            </h2>
            <p className="font-body text-sm md:text-base text-[hsl(var(--scrm-cream))]/65 max-w-md leading-relaxed">
              The list is curated. Names submitted today are considered for Drop 001 —
              Airport Club, releasing in private soon.
            </p>

            <div className="mt-10 hidden lg:block">
              <div className="border-t hairline pt-6 grid grid-cols-2 gap-6 max-w-xs">
                <div>
                  <div className="font-display text-2xl text-[hsl(var(--scrm-cream))]">
                    001
                  </div>
                  <div className="font-body text-[11px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mt-1">
                    Current Drop
                  </div>
                </div>
                <div>
                  <div className="font-display text-2xl text-[hsl(var(--scrm-cream))]">
                    LIMITED
                  </div>
                  <div className="font-body text-[11px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mt-1">
                    Quantities
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid={LANDING.memberSuccess}
                className="border hairline border-[hsl(var(--scrm-cream))]/15 p-10 md:p-14"
              >
                <div className="text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-4">
                  Confirmed · {form.email}
                </div>
                <h3 className="font-display uppercase tracking-tight text-3xl md:text-5xl text-[hsl(var(--scrm-cream))] mb-4 leading-[0.95]">
                  Welcome to<br />the club.
                </h3>
                <p className="font-body text-sm md:text-base text-[hsl(var(--scrm-cream))]/65 max-w-md">
                  Your application is on the list. Members will be notified privately when
                  Drop 001 — Airport Club opens. Watch your inbox.
                </p>
              </div>
            ) : (
              <form
                data-testid={LANDING.membersForm}
                onSubmit={submit}
                className="space-y-2"
                noValidate
              >
                <div>
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-1">
                    Full Name
                  </label>
                  <input
                    data-testid={LANDING.memberInputName}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="editorial-input"
                    autoComplete="name"
                  />
                </div>

                <div className="pt-6">
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-1">
                    Email Address
                  </label>
                  <input
                    data-testid={LANDING.memberInputEmail}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@private.club"
                    className="editorial-input"
                    autoComplete="email"
                  />
                </div>

                <div className="pt-6">
                  <label className="block text-[10px] tracking-eyebrow uppercase text-[hsl(var(--scrm-cream))]/55 mb-1">
                    Phone <span className="opacity-60">(Optional)</span>
                  </label>
                  <input
                    data-testid={LANDING.memberInputPhone}
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="+ country  · number"
                    className="editorial-input"
                    autoComplete="tel"
                  />
                </div>

                {error && (
                  <div
                    data-testid={LANDING.memberError}
                    className="pt-6 font-body text-xs text-red-300/80 tracking-wide"
                  >
                    {error}
                  </div>
                )}

                <div className="pt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
                  <button
                    type="submit"
                    data-testid={LANDING.memberSubmitBtn}
                    disabled={submitting}
                    className="btn-cream font-body uppercase tracking-[0.22em] text-xs md:text-[13px] px-10 py-5 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting…" : "Request Access →"}
                  </button>
                  <p className="font-body text-[11px] tracking-wide text-[hsl(var(--scrm-cream))]/50 max-w-xs">
                    No spam. Members-only release notifications.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
